// src/components/layout/IFoodDateFilter.jsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
    Drawer,
    Box,
    Typography,
    Stack,
    Button,
    Divider,
    IconButton,
    Paper,
    Grid,
    Chip,
    Collapse,
    CircularProgress,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
    format,
    startOfMonth,
    endOfMonth,
    setDate,
    subMonths,
    isValid,
    isSameMonth
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { useTheme, alpha } from "@mui/material/styles";
import { debugLog } from "../../utils/debug";
import { API_URL } from "../../config"; // Ensure API_URL is imported if needed, though not used in this pure logic version yet (logic moved to parent or removed usage)

const BRAND_YELLOW = "#fdb913";

// ──────────────── CONSTANTS & UTILS ────────────────

const WEEK_PRESETS = [
    { id: 'week1', label: '1ª Semana', desc: 'Dias 1 a 7', range: [1, 7] },
    { id: 'week2', label: '2ª Semana', desc: 'Dias 8 a 14', range: [8, 14] },
    { id: 'week3', label: '3ª Semana', desc: 'Dias 15 a 21', range: [15, 21] },
    { id: 'week4', label: '4ª Semana', desc: 'Dias 22 a 28', range: [22, 28] },
    { id: 'full', label: 'Mês Inteiro', desc: 'Dia 1 ao fim', range: null },
    { id: 'trimestre', label: 'Últimos 3 Meses', desc: 'Trimestre', range: null },
];

const fmt = (d) => {
    if (!d || !isValid(d)) return null;
    try {
        const date = new Date(d);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return null;
    }
};

// Pure logic for range building
const buildMonthRange = (monthDate, presetId) => {
    const base = startOfMonth(monthDate);
    const preset = WEEK_PRESETS.find(p => p.id === presetId);

    if (!preset) return { start: base, end: endOfMonth(base) };

    if (preset.id === 'full') {
        return { start: base, end: endOfMonth(base) };
    }

    if (preset.id === 'trimestre') {
        return { start: startOfMonth(subMonths(monthDate, 2)), end: endOfMonth(monthDate) };
    }

    const [dStart, dEnd] = preset.range;
    // Set days, cloning dates to match behavior
    const start = setDate(base, dStart);
    const end = setDate(base, dEnd);

    return { start, end };
};


// ══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════

export default function IFoodDateFilter({ onApply }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    // const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Unused

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Filter State
    const [month1, setMonth1] = useState(subMonths(new Date(), 1)); // Mês Anterior
    const [month2, setMonth2] = useState(new Date());               // Mês Atual

    // Manual State (Advanced)
    const [customStart1, setCustomStart1] = useState(null);
    const [customEnd1, setCustomEnd1] = useState(null);
    const [customStart2, setCustomStart2] = useState(null);
    const [customEnd2, setCustomEnd2] = useState(null);

    const [activePreset, setActivePreset] = useState(null); // 'week1' | ... | null
    const [showAdvanced, setShowAdvanced] = useState(false);

    const [applied, setApplied] = useState(null);

    // ──────────────── INIT ────────────────
    // Tenta carregar do localStorage apenas para sincronizar o estado interno (chip visual)
    useEffect(() => {
        try {
            const saved = localStorage.getItem('vochica_filter');
            if (saved) {
                const parsed = JSON.parse(saved);
                setApplied(parsed);
                // 🔴 REMOVIDO: onApply(parsed) 
                // A página pai agora é responsável por carregar seus próprios dados iniciais
                // para evitar loops infinitos de renderização.
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    // ──────────────── HANDLERS ────────────────

    const handleApplyPreset = async (presetId, m1 = month1, m2 = month2) => {
        setLoading(true);
        setActivePreset(presetId);
        // Clear custom
        setCustomStart1(null); setCustomEnd1(null);
        setCustomStart2(null); setCustomEnd2(null);

        const range1 = buildMonthRange(m1, presetId);
        const range2 = m2 ? buildMonthRange(m2, presetId) : null;

        const presetLabel = WEEK_PRESETS.find(p => p.id === presetId)?.label || "Personalizado";
        const m1Label = format(m1, "MMM/yy", { locale: ptBR });
        const m2Label = m2 ? format(m2, "MMM/yy", { locale: ptBR }) : "";

        let finalLabel = m1Label;
        if (m2Label) finalLabel += ` vs ${m2Label}`;

        const payload = {
            type: 'preset',
            preset: presetId,
            start: fmt(range1.start),
            end: fmt(range1.end),
            start2: range2 ? fmt(range2.start) : null,
            end2: range2 ? fmt(range2.end) : null,

            label: finalLabel,
            resumo: `${presetLabel}`,

            label1: `${m1Label} (${format(range1.start, 'dd')}-${format(range1.end, 'dd')})`,
            label2: range2 ? `${m2Label} (${format(range2.start, 'dd')}-${format(range2.end, 'dd')})` : null,

            // Metadata for restoring state
            meta: {
                month1: m1.toISOString(),
                month2: m2 ? m2.toISOString() : null,
                preset: presetId
            }
        };

        debugLog("📤 Aplicando preset:", payload);

        await new Promise(r => setTimeout(r, 300));

        setApplied(payload);
        setOpen(false);
        localStorage.setItem('vochica_filter', JSON.stringify(payload));

        window.dispatchEvent(
            new CustomEvent("vochica:filters-applied", { detail: payload })
        );

        if (onApply) onApply(payload);
        setLoading(false);
    };

    const handleApplyCustom = async () => {
        if (!customStart1 || !customEnd1) {
            return;
        }
        setLoading(true);
        setActivePreset(null);

        const isPeriod2Valid = customStart2 && customEnd2;

        const payload = {
            type: "custom",
            start: fmt(customStart1),
            end: fmt(customEnd1),
            start2: isPeriod2Valid ? fmt(customStart2) : null,
            end2: isPeriod2Valid ? fmt(customEnd2) : null,

            label: "Personalizado",
            resumo: "Personalizado",

            label1: `${format(customStart1, "dd/MM")} - ${format(customEnd1, "dd/MM")}`,
            label2: isPeriod2Valid ? `${format(customStart2, "dd/MM")} - ${format(customEnd2, "dd/MM")}` : null,
        };

        await new Promise(r => setTimeout(r, 300));

        setApplied(payload);
        setOpen(false);
        localStorage.setItem('vochica_filter', JSON.stringify(payload));
        window.dispatchEvent(new CustomEvent("vochica:filters-applied", { detail: payload }));
        if (onApply) onApply(payload);
        setLoading(false);
    };

    // ──────────────── RENDERERS ────────────────

    const PresetButton = ({ preset }) => {
        const isSelected = activePreset === preset.id;
        return (
            <Paper
                elevation={isSelected ? (theme.palette.mode === 'dark' ? 8 : 4) : 1}
                onClick={() => handleApplyPreset(preset.id)}
                sx={{
                    p: 2,
                    cursor: 'pointer',
                    bgcolor: isSelected
                        ? (theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.15) : '#fff')
                        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#fafafa'),
                    border: isSelected ? `2px solid ${BRAND_YELLOW}` : '1px solid',
                    borderColor: isSelected ? BRAND_YELLOW : 'divider',
                    borderRadius: '8px',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        borderColor: BRAND_YELLOW,
                        bgcolor: isSelected ? undefined : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f0f0')
                    }
                }}
            >
                <Typography variant="body2" fontWeight={700} color={isSelected ? "primary" : "text.primary"}>
                    {preset.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {preset.desc}
                </Typography>
            </Paper>
        );
    };

    const drawerContent = (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        width: isMobile ? "100%" : 450,
                        bgcolor: "background.paper",
                        backgroundImage: 'none'
                    }
                }}
            >
                {/* HEAD */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Box>
                        <Typography variant="h6" fontWeight={800} color="text.primary">
                            Filtros de Data
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Defina os meses e o recorte temporal
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>

                    {/* 1. MONTH SELECTORS */}
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
                        📅 1. Selecione os Meses Base
                    </Typography>

                    <Stack spacing={2} sx={{ mb: 4 }}>
                        <DatePicker
                            label="Mês Período 1 (Referência)"
                            views={['month', 'year']}
                            value={month1}
                            onChange={(v) => {
                                setMonth1(v);
                                if (activePreset && v) handleApplyPreset(activePreset, v, month2);
                            }}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                        <DatePicker
                            label="Mês Período 2 (Opcional - Comparação)"
                            views={['month', 'year']}
                            value={month2}
                            onChange={(v) => {
                                setMonth2(v);
                                if (activePreset && month1) handleApplyPreset(activePreset, month1, v);
                            }}
                            slotProps={{
                                textField: { fullWidth: true, size: 'small' },
                                actionBar: { actions: ['clear', 'accept'] }
                            }}
                        />
                    </Stack>

                    <Divider sx={{ mb: 3 }} />

                    {/* 2. PRESETS GRID */}
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
                        ✂️ 2. Escolha o Recorte (Aplica aos 2 meses)
                    </Typography>

                    <Grid container spacing={1.5}>
                        {WEEK_PRESETS.map(p => (
                            <Grid item xs={6} key={p.id}>
                                <PresetButton preset={p} />
                            </Grid>
                        ))}
                    </Grid>

                    <Divider sx={{ my: 4 }} />

                    {/* 3. ADVANCED */}
                    <Button
                        fullWidth
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ color: 'text.secondary', textTransform: 'none', justifyContent: 'space-between' }}
                    >
                        Opções Avançadas (Intervalo Livre)
                    </Button>

                    <Collapse in={showAdvanced}>
                        <Paper sx={{ p: 2, mt: 1, border: '1px dashed', borderColor: 'divider', bgcolor: 'background.default' }}>
                            <Typography variant="caption" display="block" sx={{ mb: 2, color: 'error.main' }}>
                                ⚠️ O modo avançado ignora os meses selecionados acima.
                            </Typography>

                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="caption" fontWeight={700}>Período 1</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <DatePicker
                                            label="Início" value={customStart1} onChange={setCustomStart1}
                                            slotProps={{ textField: { size: 'small' } }}
                                        />
                                        <DatePicker
                                            label="Fim" value={customEnd1} onChange={setCustomEnd1}
                                            slotProps={{ textField: { size: 'small' } }}
                                        />
                                    </Stack>
                                </Box>
                                <Box>
                                    <Typography variant="caption" fontWeight={700}>Período 2 (Opcional)</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <DatePicker
                                            label="Início" value={customStart2} onChange={setCustomStart2}
                                            slotProps={{
                                                textField: { size: 'small' },
                                                actionBar: { actions: ['clear', 'accept'] }
                                            }}
                                        />
                                        <DatePicker
                                            label="Fim" value={customEnd2} onChange={setCustomEnd2}
                                            slotProps={{
                                                textField: { size: 'small' },
                                                actionBar: { actions: ['clear', 'accept'] }
                                            }}
                                        />
                                    </Stack>
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={handleApplyCustom}
                                    disabled={!customStart1 || !customEnd1}
                                    sx={{ bgcolor: BRAND_YELLOW, color: '#000', fontWeight: 'bold' }}
                                >
                                    Aplicar Personalizado
                                </Button>
                            </Stack>
                        </Paper>
                    </Collapse>

                </Box>
            </Drawer>
        </LocalizationProvider>
    );

    // FOOTER
    return (
        <>
            {drawerContent}

            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: { xs: 0, md: "260px" }, // Sync with sidebar width
                    right: 0,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(22, 27, 34, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(12px)',
                    borderTop: "1px solid",
                    borderColor: "divider",
                    height: { xs: 64, md: 72 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: { xs: 1.5, md: 4 },
                    zIndex: 1100,
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? "0 -4px 20px rgba(0,0,0,0.4)" : "0 -2px 10px rgba(0,0,0,0.05)",
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, overflow: 'hidden' }}>
                    {!isMobile && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            Filtro Ativo:
                        </Typography>
                    )}
                    {applied ? (
                        <Chip
                            icon={!isMobile ? <CalendarMonthIcon fontSize="small" /> : null}
                            label={applied.resumo || applied.label}
                            size={isMobile ? "small" : "medium"}
                            sx={{ 
                                bgcolor: BRAND_YELLOW, 
                                px: { xs: 0.5, md: 1 }, 
                                fontWeight: 700,
                                fontSize: { xs: '0.75rem', md: '0.875rem' },
                                maxWidth: { xs: '140px', sm: 'none' }
                            }}
                        />
                    ) : (
                        <Chip label="Sem filtro" size="small" />
                    )}
                </Box>

                <Button
                    variant="contained"
                    size={isMobile ? "small" : "medium"}
                    startIcon={<DateRangeIcon />}
                    onClick={() => setOpen(true)}
                    sx={{
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.main : "#222",
                        color: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : "#fff",
                        textTransform: 'none',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        minWidth: 'fit-content',
                        borderRadius: 2,
                        '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.dark : "#000" }
                    }}
                >
                    {isMobile ? 'Datas' : 'Alterar Datas'}
                </Button>
            </Box>
        </>
    );
}
